import toastEmitter from "../utils/toastEmitter";
import React from "react";
import { Toast, ToastTitle, ToastBody, ToastTrigger } from "@fluentui/react-toast";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { getIntl } from "../utils/intl";
import { logMessages } from "../utils/messages";
import { Button } from "@fluentui/react-components";

/**
 * Usage example:
 * notificationsCenter.success(intl.formatMessage({ id: "room-settings-sidebar.fly-mode-enabled", defaultMessage: "Fly Mode Enabled" }));
 * notificationsCenter.info(LogMessageType.audioSuspended);
 */

const dismissButton = <Button icon={<Dismiss20Regular />} size="small" appearance="transparent" />;

/**
 * Notifications Center Class
 */

const customToastBodyStyle = {
  color: "var(--Gray-4, #BDBDBD)"
};

const toastBodyEllipsisStyle = {
  display: "-webkit-box",
  WebkitLineClamp: 5, // Limit to 5 lines
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: "16px"
};

class NotificationsCenter {
  /**
   * Push a custom toast
   * @param {React.ReactElement} toastContent - Custom JSX content for the toast
   * @param {Object} options - Optional settings (e.g., intent)
   */
  push(toastContent, options = {}) {
    toastEmitter.emit("toast", toastContent, options);
  }

  /**
   * Push a success toast with customizable title and optional message
   * @param {string} title - The title to display
   * @param {string} [message] - The optional message to display
   * @param {Object} options - Optional settings
   */
  success(title, message, options = {}) {
    const intl = getIntl();

    // Title must be searched in the logMessages object, if not found, use the title parameter
    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title;

    const toastContent = (
      <Toast>
        <ToastTitle intent="success" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={customToastBodyStyle}>{message}</ToastBody>}
      </Toast>
    );

    this.push(toastContent, { intent: "success", ...options });
  }

  /**
   * Push an error toast with customizable title and optional message
   * @param {string} title - The title to display
   * @param {string} [message] - The optional message to display
   * @param {Object} options - Optional settings
   */
  error(title, message, options = {}) {
    const intl = getIntl();

    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Error";

    const toastContent = (
      <Toast>
        <ToastTitle intent="error" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody>{message}</ToastBody>}
      </Toast>
    );

    this.push(toastContent, { intent: "error", ...options });
  }

  /**
   * Push an info toast with customizable title and optional message
   * @param {string} title - The title to display
   * @param {string} [message] - The optional message to display
   * @param {Object} options - Optional settings
   */
  info(title, message, options = {}) {
    const intl = getIntl();

    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Info";

    const notificationMessage =
      message && logMessages[message] ? intl.formatMessage(logMessages[message], { defaultMessage: message }) : message;

    const toastContent = (
      <Toast>
        <ToastTitle style={{ display: "block" }} intent="info" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={customToastBodyStyle}>{notificationMessage}</ToastBody>}
      </Toast>
    );

    this.push(toastContent, { intent: "info", ...options });
  }

  /**
   * Push a warning toast with customizable title and optional message
   * @param {string} title - The title to display
   * @param {string} [message] - The optional message to display
   * @param {Object} options - Optional settings
   */
  warning(title, message, options = {}) {
    const intl = getIntl();

    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Warning";

    const notificationMessage =
      message && logMessages[message] ? intl.formatMessage(logMessages[message], { defaultMessage: message }) : message;

    const toastContent = (
      <Toast>
        <ToastTitle intent="warning" action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {notificationMessage && <ToastBody>{notificationMessage}</ToastBody>}
      </Toast>
    );

    this.push(toastContent, { intent: "warning", ...options });
  }

  withMedia(media, title, message, options = {}) {
    const intl = getIntl();

    const notificationTitle = logMessages[title]
      ? intl.formatMessage(logMessages[title], { defaultMessage: title })
      : title || "Info";

    const toastContent = (
      <Toast className="toast-container">
        <ToastTitle media={media} action={<ToastTrigger>{dismissButton}</ToastTrigger>}>
          {notificationTitle}
        </ToastTitle>
        {message && <ToastBody style={toastBodyEllipsisStyle}>{message}</ToastBody>}
      </Toast>
    );

    this.push(toastContent, { ...options });
  }

  dismiss(id) {
    toastEmitter.emit("dismissToast", id);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    toastEmitter.emit("dismissAll");
  }
}

// Exporting a singleton instance
const notificationsCenter = new NotificationsCenter();
window.notificationsCenter = notificationsCenter;

export default notificationsCenter;
